import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../shared/TopBar';
import { sqlApi, EmployeeCreate, Employee, Task, TaskFilters, TaskDefinition, TaskCreate, TaskUpdate } from '../../services/sqlApi';
import { API_BASE_URL } from '../../services/apiConfig';
import './AdminConsole.css';

interface AdminConsoleProps {
  currentUser: string;
  onLogout: () => void;
}

const workspaceSections = [
  {
    title: 'Workspace',
    links: [
      { label: 'Live Sessions', description: 'Monitor assistants in real time', badge: '3 active' },
      { label: 'Task Queue', description: 'Review pending submissions', badge: '12 waiting' },
      { label: 'Transcript Archive', description: 'Audit past conversations' },
    ],
  },
  {
    title: 'Analytics',
    links: [
      { label: 'Performance Insights', description: 'Quality + SLA tracking' },
      { label: 'Billing Reports', description: 'Exports & invoices' },
    ],
  },
];

const highlightCards = [
  { label: 'Assistants Online', value: '18', trend: '+2 since 09:00' },
  { label: 'Tasks Reviewed', value: '46', trend: 'Today' },
];

type TabType = 'employees' | 'tasks';

const AdminConsole: React.FC<AdminConsoleProps> = ({ currentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState<TabType>('employees');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showAdminConfirmationModal, setShowAdminConfirmationModal] = useState(false);
  const [adminConfirmationText, setAdminConfirmationText] = useState('');
  const [employeeForm, setEmployeeForm] = useState<EmployeeCreate>({
    name: '',
    surname: '',
    role: 'employee',
    external_id: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [employeeToResetPassword, setEmployeeToResetPassword] = useState<Employee | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // Task management state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [taskFilters, setTaskFilters] = useState<{
    date: string;
    employeeId: string;
    location: string;
    deleted: 'all' | 'deleted' | 'non-deleted';
  }>({
    date: '',
    employeeId: '',
    location: '',
    deleted: 'non-deleted',
  });
  const [employeeDeletedFilter, setEmployeeDeletedFilter] = useState<'non-deleted' | 'deleted' | 'all'>('non-deleted');
  const [showEmployeeDeletedFilterDropdown, setShowEmployeeDeletedFilterDropdown] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinition[]>([]);
  const [isLoadingTaskDefinitions, setIsLoadingTaskDefinitions] = useState(false);
  const [taskForm, setTaskForm] = useState<TaskCreate>({
    task_code: '',
    quantity: 0,
    employee_id: 0,
    location: null,
    forced_submit: false,
    comments: null,
  });
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const taskDropdownRef = useRef<HTMLDivElement>(null);
  const [showEmployeeFilterDropdown, setShowEmployeeFilterDropdown] = useState(false);
  const [showStatusFilterDropdown, setShowStatusFilterDropdown] = useState(false);
  const [showEmployeeTaskDropdown, setShowEmployeeTaskDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const employeeFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const employeeTaskRef = useRef<HTMLDivElement>(null);
  const employeeDeletedFilterRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState<{ quantity: number; location: string | null; comments: string | null }>({
    quantity: 0,
    location: null,
    comments: null,
  });
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [invoiceErrorMessage, setInvoiceErrorMessage] = useState<string | null>(null);
  
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const fetchEmployees = async () => {
    setIsLoadingEmployees(true);
    try {
      const includeDeleted = employeeDeletedFilter === 'all' || employeeDeletedFilter === 'deleted';
      const data = await sqlApi.getAllEmployees(includeDeleted);
      
      // Filter by deleted status on frontend
      let filteredData = data;
      if (employeeDeletedFilter === 'deleted') {
        filteredData = data.filter((emp: Employee) => emp.is_deleted === true);
      } else if (employeeDeletedFilter === 'non-deleted') {
        filteredData = data.filter((emp: Employee) => !emp.is_deleted);
      }
      // 'all' shows everything, no additional filtering needed
      
      setEmployees(filteredData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const fetchTasks = async (customFilters?: typeof taskFilters) => {
    setIsLoadingTasks(true);
    try {
      const filtersToUse = customFilters !== undefined ? customFilters : taskFilters;
      const filters: TaskFilters = {
        include_deleted: filtersToUse.deleted === 'all' || filtersToUse.deleted === 'deleted',
      };
      
      if (filtersToUse.employeeId) {
        filters.employee_id = [parseInt(filtersToUse.employeeId)];
      }
      
      if (filtersToUse.date) {
        filters.date = [filtersToUse.date];
      }
      
      const data = await sqlApi.getAllTasks(filters);
      
      // Filter by location on frontend if provided
      let filteredData = data;
      if (filtersToUse.location) {
        const locationLower = filtersToUse.location.toLowerCase();
        filteredData = data.filter((task: Task) => 
          task.location && task.location.toLowerCase().includes(locationLower)
        );
      }
      
      // Filter by deleted status on frontend
      if (filtersToUse.deleted === 'deleted') {
        filteredData = filteredData.filter((task: Task) => task.is_deleted === true);
      } else if (filtersToUse.deleted === 'non-deleted') {
        filteredData = filteredData.filter((task: Task) => task.is_deleted === false);
      }
      // 'all' shows everything, no additional filtering needed
      
      setTasks(filteredData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const fetchTaskDefinitions = async () => {
    setIsLoadingTaskDefinitions(true);
    try {
      const data = await sqlApi.getAllTaskDefinitions();
      setTaskDefinitions(data);
    } catch (error) {
      console.error('Error fetching task definitions:', error);
      setTaskDefinitions([]);
    } finally {
      setIsLoadingTaskDefinitions(false);
    }
  };

  const handleOpenAddTaskModal = () => {
    setShowAddTaskModal(true);
    setTaskSearchQuery('');
    setTaskForm({
      task_code: '',
      quantity: 0,
      employee_id: 0,
      location: null,
      forced_submit: false,
      comments: null,
    });
    setShowEmployeeTaskDropdown(false);
    // Open dropdown when modal opens if we have task definitions
    if (taskDefinitions.length > 0) {
      setShowTaskDropdown(true);
    }
  };

  useEffect(() => {
    if (activeTab === 'employees') {
      fetchEmployees();
    } else if (activeTab === 'tasks') {
      fetchEmployees(); // Load employees for the dropdown
      fetchTasks();
      fetchTaskDefinitions(); // Load task definitions for the add task modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, employeeDeletedFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(event.target as Node)) {
        setShowTaskDropdown(false);
      }
      if (employeeFilterRef.current && !employeeFilterRef.current.contains(event.target as Node)) {
        setShowEmployeeFilterDropdown(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node)) {
        setShowStatusFilterDropdown(false);
      }
      if (employeeTaskRef.current && !employeeTaskRef.current.contains(event.target as Node)) {
        setShowEmployeeTaskDropdown(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setShowRoleDropdown(false);
      }
      if (employeeDeletedFilterRef.current && !employeeDeletedFilterRef.current.contains(event.target as Node)) {
        setShowEmployeeDeletedFilterDropdown(false);
      }
    };

    if (showTaskDropdown || showEmployeeFilterDropdown || showStatusFilterDropdown || showEmployeeTaskDropdown || showRoleDropdown || showEmployeeDeletedFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTaskDropdown, showEmployeeFilterDropdown, showStatusFilterDropdown, showEmployeeTaskDropdown, showRoleDropdown, showEmployeeDeletedFilterDropdown]);

  // Open dropdown when task definitions are loaded and modal is open
  useEffect(() => {
    if (showAddTaskModal && taskDefinitions.length > 0 && !taskSearchQuery) {
      setShowTaskDropdown(true);
    }
  }, [showAddTaskModal, taskDefinitions.length, taskSearchQuery]);

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If admin role is selected, show confirmation modal first
    if (employeeForm.role === 'admin') {
      setShowAdminConfirmationModal(true);
      return;
    }

    // Proceed with employee creation
    await submitEmployeeCreation();
  };

  const submitEmployeeCreation = async () => {
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const response = await sqlApi.addEmployee(employeeForm);
      setSubmitMessage({ type: 'success', text: `Employee created successfully! ID: ${response.employee_id}` });
      // Reset form
      setEmployeeForm({
        name: '',
        surname: '',
        role: 'employee',
        external_id: null,
      });
      // Refresh employees list and close modals after 2 seconds
      fetchEmployees();
      setTimeout(() => {
        setShowAddEmployeeModal(false);
        setShowAdminConfirmationModal(false);
        setSubmitMessage(null);
        setAdminConfirmationText('');
      }, 2000);
    } catch (error) {
      setSubmitMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to create employee' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdminConfirmation = () => {
    const requiredText = 'yes, i want to create an admin account';
    if (adminConfirmationText.trim().toLowerCase() === requiredText) {
      setShowAdminConfirmationModal(false);
      setAdminConfirmationText('');
      submitEmployeeCreation();
    }
  };

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;

    setIsDeleting(true);
    setSubmitMessage(null); // Clear any previous messages
    try {
      await sqlApi.removeEmployee(employeeToDelete.id);
      setEmployeeToDelete(null);
      setSubmitMessage({ 
        type: 'success', 
        text: `Employee ${employeeToDelete.name} ${employeeToDelete.surname} deleted successfully.` 
      });
      // Refresh the employee list
      await fetchEmployees();
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      console.error('Error deleting employee:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete employee';
      setSubmitMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      // Keep the modal open so user can see the error - don't close it
      // Don't set employeeToDelete to null so modal stays open
      setTimeout(() => setSubmitMessage(null), 10000);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!employeeToResetPassword) return;

    setIsResettingPassword(true);
    try {
      await sqlApi.resetEmployeePassword(employeeToResetPassword.id);
      setEmployeeToResetPassword(null);
      // Refresh the employee list
      await fetchEmployees();
      setSubmitMessage({ 
        type: 'success', 
        text: `Password reset for ${employeeToResetPassword.name} ${employeeToResetPassword.surname}. They will need to set a new password.` 
      });
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      console.error('Error resetting password:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to reset password' 
      });
      setTimeout(() => setSubmitMessage(null), 5000);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCloseModal = () => {
    if (!isSubmitting && !isDeleting && !isResettingPassword) {
      setShowAddEmployeeModal(false);
      setShowAdminConfirmationModal(false);
      setEmployeeToResetPassword(null);
      setEmployeeForm({
        name: '',
        surname: '',
        role: 'employee',
        external_id: null,
      });
      setSubmitMessage(null);
      setAdminConfirmationText('');
      setEmployeeToDelete(null);
      setShowAddTaskModal(false);
      setTaskForm({
        task_code: '',
        quantity: 0,
        employee_id: 0,
        location: null,
        forced_submit: false,
        comments: null,
      });
      setTaskSearchQuery('');
      setShowTaskDropdown(false);
      setShowEmployeeTaskDropdown(false);
      setShowRoleDropdown(false);
      setShowEditTaskModal(false);
      setTaskToEdit(null);
      setTaskToDelete(null);
      setDeleteErrorMessage(null);
      setShowInvoiceModal(false);
      setInvoiceErrorMessage(null);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const response = await sqlApi.addTask(taskForm);
      setSubmitMessage({ type: 'success', text: `Task created successfully! ID: ${response.task_id}` });
      // Reset form
      setTaskForm({
        task_code: '',
        quantity: 0,
        employee_id: 0,
        location: null,
        forced_submit: false,
        comments: null,
      });
      setTaskSearchQuery('');
      // Refresh tasks list and close modal after 2 seconds
      fetchTasks();
      setTimeout(() => {
        setShowAddTaskModal(false);
        setSubmitMessage(null);
      }, 2000);
    } catch (error) {
      setSubmitMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to create task' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTaskDefinitions = taskSearchQuery
    ? taskDefinitions.filter((def) => {
        const searchLower = taskSearchQuery.toLowerCase();
        return (
          def.task_code.toLowerCase().includes(searchLower) ||
          def.beschrijving.toLowerCase().includes(searchLower)
        );
      })
    : taskDefinitions; // Show all tasks when search is empty

  const handleSelectTask = (taskDef: TaskDefinition) => {
    setTaskForm({ ...taskForm, task_code: taskDef.task_code });
    setTaskSearchQuery(`${taskDef.task_code} - ${taskDef.beschrijving}`);
    setShowTaskDropdown(false);
  };

  const handleOpenEditTaskModal = (task: Task) => {
    setTaskToEdit(task);
    setEditTaskForm({
      quantity: task.quantity,
      location: task.location || null,
      comments: task.comments || null,
    });
    setShowEditTaskModal(true);
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskToEdit) return;

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const updateData: TaskUpdate = {};
      if (editTaskForm.quantity !== taskToEdit.quantity) {
        updateData.quantity = editTaskForm.quantity;
      }
      if (editTaskForm.location !== taskToEdit.location) {
        updateData.location = editTaskForm.location;
      }
      if (editTaskForm.comments !== taskToEdit.comments) {
        updateData.comments = editTaskForm.comments;
      }

      if (Object.keys(updateData).length === 0) {
        setSubmitMessage({ type: 'error', text: 'No changes to save' });
        setIsSubmitting(false);
        return;
      }

      await sqlApi.updateTask(taskToEdit.id, updateData);
      setSubmitMessage({ type: 'success', text: 'Task updated successfully!' });
      
      // Refresh tasks list
      fetchTasks();
      
      setTimeout(() => {
        setShowEditTaskModal(false);
        setTaskToEdit(null);
        setSubmitMessage(null);
      }, 2000);
    } catch (error) {
      setSubmitMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to update task' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTaskClick = (task: Task) => {
    setTaskToDelete(task);
    setDeleteErrorMessage(null);
  };

  const handleConfirmDeleteTask = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    setDeleteErrorMessage(null);
    try {
      await sqlApi.deleteTask(taskToDelete.id);
      // Refresh tasks list
      await fetchTasks();
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      setDeleteErrorMessage(error instanceof Error ? error.message : 'Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportToInvoice = async () => {
    if (!invoiceDateRange.startDate || !invoiceDateRange.endDate) {
      setInvoiceErrorMessage('Please select both start and end dates');
      return;
    }

    if (new Date(invoiceDateRange.startDate) > new Date(invoiceDateRange.endDate)) {
      setInvoiceErrorMessage('Start date must be before or equal to end date');
      return;
    }

    setIsGeneratingInvoice(true);
    setInvoiceErrorMessage(null);

    try {
      // Fetch all non-deleted tasks (we'll filter by date range on frontend)
      const filters: TaskFilters = {
        include_deleted: false,
      };
      
      const allTasks = await sqlApi.getAllTasks(filters);
      
      // Filter tasks by date range on frontend
      const filteredTasks = allTasks.filter((task: Task) => {
        const taskDate = new Date(task.created_at).toISOString().split('T')[0];
        return taskDate >= invoiceDateRange.startDate && taskDate <= invoiceDateRange.endDate;
      });

      if (filteredTasks.length === 0) {
        setInvoiceErrorMessage('No tasks found for the selected date range');
        setIsGeneratingInvoice(false);
        return;
      }

      // Get task definitions to map task codes to descriptions, units, and prices
      const taskDefsMap = new Map<string, TaskDefinition>();
      taskDefinitions.forEach(def => {
        taskDefsMap.set(def.task_code, def);
      });

      // Transform tasks to invoice format
      const invoiceRows: Array<Record<string, any>> = [];
      filteredTasks.forEach((task: Task) => {
        const taskDef = taskDefsMap.get(task.task_code);
        if (taskDef) {
          invoiceRows.push({
            'BESTEKS POST- NUMMER': task.task_code,
            'OMSCHRIJVING': taskDef.beschrijving || '',
            'EEN-HEID': taskDef.eenheid || '',
            'HOEVEELHEID RESULTAATS- VERPLICHTING': taskDef.hoeveelheid_resultaats_verplichting || '',
            'VERWERKTE HOEVEELHEID IN TERMIJN': task.quantity.toString(),
            'PRIJS PER EENHEID': taskDef.prijs_per_eenheid_in_euro.toString(),
          });
        }
      });

      if (invoiceRows.length === 0) {
        setInvoiceErrorMessage('No valid tasks found with task definitions');
        setIsGeneratingInvoice(false);
        return;
      }

      // Call FastAPI backend to generate invoice
      const response = await fetch(`${API_BASE_URL}/generate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceRows),
      });

      if (!response.ok) {
        let message = 'Failed to generate invoice';
        try {
          const contentType = response.headers.get('content-type') || '';
          const text = await response.text();
          if (contentType.includes('application/json')) {
            const json = JSON.parse(text);
            message = (json.detail ?? json.message ?? json.error?.message ?? text) || message;
            if (Array.isArray(message)) message = message.map((e: any) => e.msg ?? e).join('; ');
          } else if (text) {
            message = text;
          }
        } catch (_) {
          // keep default message
        }
        throw new Error(message);
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `Invoice_${invoiceDateRange.startDate}_to_${invoiceDateRange.endDate}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Close modal after successful download
      setShowInvoiceModal(false);
      setInvoiceDateRange({ startDate: '', endDate: '' });
    } catch (error) {
      console.error('Error generating invoice:', error);
      setInvoiceErrorMessage(error instanceof Error ? error.message : 'Failed to generate invoice');
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const selectedTaskDefinition = taskDefinitions.find(def => def.task_code === taskForm.task_code);

  return (
    <div className="admin-console-page">
      {/* Mobile sidebar toggle button */}
      <button
        type="button"
        className="admin-sidebar-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle sidebar"
      >
        {isSidebarOpen ? '✕' : '☰'}
      </button>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="admin-sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        {/* Mobile close button inside sidebar */}
        <button
          type="button"
          className="admin-sidebar-close"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          ✕
        </button>
        <div className="admin-sidebar__header">
          <span className="admin-chip">Admin</span>
          <h1>Control Center</h1>
          <p>Central overview for Werkbuddy supervisors</p>
        </div>

        <div className="admin-user-card">
          <div>
            <span className="admin-user-label">Signed in as</span>
            <p className="admin-user-name">{currentUser}</p>
            <span className="admin-user-meta">{today}</span>
          </div>
          <div className="admin-status-pill">
            <span className="status-indicator" />
            <span>online</span>
          </div>
        </div>

        <div className="admin-metrics">
          {highlightCards.map((card) => (
            <div key={card.label} className="admin-metrics__card">
              <span className="label">{card.label}</span>
              <strong>{card.value}</strong>
              <span className="meta">{card.trend}</span>
            </div>
          ))}
        </div>

        <div className="admin-nav">
          {workspaceSections.map((section) => (
            <div key={section.title} className="admin-nav__section">
              <h3>{section.title}</h3>
              <ul>
                {section.links.map((link) => (
                  <li key={link.label}>
                    <button 
                      type="button" 
                      className="admin-nav__item"
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      <div>
                        <span className="title">{link.label}</span>
                        <span className="description">{link.description}</span>
                      </div>
                      {link.badge && <span className="badge">{link.badge}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Sidebar footer reserved for future actions (currently empty) */}
        <div className="admin-sidebar__footer" />
      </aside>

      <div className="admin-main-content">
        <TopBar
          lang="en"
          onLogout={onLogout}
          showLanguageToggle={false}
          showTtsToggle={false}
          showThemeToggle={true}
        />
        <div className="admin-content-wrapper">
          <div className="admin-tabs">
            <button
              type="button"
              className={`admin-tab ${activeTab === 'employees' ? 'active' : ''}`}
              onClick={() => setActiveTab('employees')}
            >
              Employees Management
            </button>
            <button
              type="button"
              className={`admin-tab ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveTab('tasks')}
            >
              Task Management
            </button>
          </div>

          <div className="admin-tab-content">
            {activeTab === 'employees' && (
              <div className="admin-tab-panel">
                <div className="admin-panel-header">
                  <h2>Employee Overview</h2>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div className="admin-filter-group admin-filter-group-inline" style={{ margin: 0 }}>
                      <span className="admin-filter-label-inline">Status</span>
                      <div className="admin-filter-dropdown" ref={employeeDeletedFilterRef}>
                        <button
                          type="button"
                          className={`admin-filter-dropdown-trigger ${showEmployeeDeletedFilterDropdown ? 'open' : ''}`}
                          onClick={() => setShowEmployeeDeletedFilterDropdown(!showEmployeeDeletedFilterDropdown)}
                        >
                          {employeeDeletedFilter === 'non-deleted' ? 'Non-deleted only' 
                            : employeeDeletedFilter === 'deleted' ? 'Deleted only' 
                            : 'All employees'}
                        </button>
                        {showEmployeeDeletedFilterDropdown && (
                          <div className="admin-filter-dropdown-menu">
                            <div
                              className={`admin-filter-dropdown-item ${employeeDeletedFilter === 'non-deleted' ? 'selected' : ''}`}
                              onClick={() => {
                                setEmployeeDeletedFilter('non-deleted');
                                setShowEmployeeDeletedFilterDropdown(false);
                              }}
                            >
                              Non-deleted only
                            </div>
                            <div
                              className={`admin-filter-dropdown-item ${employeeDeletedFilter === 'deleted' ? 'selected' : ''}`}
                              onClick={() => {
                                setEmployeeDeletedFilter('deleted');
                                setShowEmployeeDeletedFilterDropdown(false);
                              }}
                            >
                              Deleted only
                            </div>
                            <div
                              className={`admin-filter-dropdown-item ${employeeDeletedFilter === 'all' ? 'selected' : ''}`}
                              onClick={() => {
                                setEmployeeDeletedFilter('all');
                                setShowEmployeeDeletedFilterDropdown(false);
                              }}
                            >
                              All employees
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="admin-secondary-btn"
                      onClick={fetchEmployees}
                      disabled={isLoadingEmployees}
                    >
                      {isLoadingEmployees ? 'Refreshing...' : '↻ Refresh'}
                    </button>
                    <button 
                      type="button" 
                      className="admin-primary-btn"
                      onClick={() => setShowAddEmployeeModal(true)}
                    >
                      + Add New Employee
                    </button>
                  </div>
                </div>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Deleted</th>
                        <th>Active Jobs</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingEmployees ? (
                        <tr>
                          <td colSpan={5} className="admin-table-empty">
                            Loading employees...
                          </td>
                        </tr>
                      ) : employees.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="admin-table-empty">
                            No employees found
                          </td>
                        </tr>
                      ) : (
                        employees.map((employee) => (
                          <tr key={employee.id}>
                            <td>{employee.name} {employee.surname}</td>
                            <td>
                              <span className={`admin-status-badge ${employee.role === 'admin' ? 'admin' : 'employee'}`}>
                                {employee.role || 'employee'}
                              </span>
                            </td>
                            <td>
                              <span className={`admin-status-badge ${employee.is_deleted ? 'deleted' : 'active'}`}>
                                {employee.is_deleted ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>-</td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                  type="button" 
                                  className="admin-action-btn admin-action-btn-secondary"
                                  onClick={() => setEmployeeToResetPassword(employee)}
                                  disabled={isResettingPassword || isDeleting}
                                  title="Reset password"
                                >
                                  Reset Password
                                </button>
                                <button 
                                  type="button" 
                                  className="admin-action-btn"
                                  onClick={() => setEmployeeToDelete(employee)}
                                  disabled={isDeleting || isResettingPassword}
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="admin-tab-panel">
                <div className="admin-panel-header">
                  <h2>Task Management</h2>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      type="button" 
                      className="admin-secondary-btn"
                      onClick={() => fetchTasks()}
                      disabled={isLoadingTasks}
                    >
                      {isLoadingTasks ? 'Refreshing...' : '↻ Refresh'}
                    </button>
                    <button 
                      type="button" 
                      className="admin-primary-btn"
                      onClick={handleOpenAddTaskModal}
                    >
                      + Add New Task
                    </button>
                    <button
                      type="button"
                      className="admin-primary-btn"
                      onClick={() => {
                        setShowInvoiceModal(true);
                        setInvoiceErrorMessage(null);
                        // Set default dates (today and 30 days ago)
                        const today = new Date().toISOString().split('T')[0];
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        const startDate = thirtyDaysAgo.toISOString().split('T')[0];
                        setInvoiceDateRange({ startDate, endDate: today });
                      }}
                      disabled={isLoadingTasks || isGeneratingInvoice}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Export to Invoice
                    </button>
                  </div>
                </div>
                <div className="admin-filters">
                  <div className="admin-filter-group">
                    <label htmlFor="filter-date">Date</label>
                    <input
                      type="date"
                      id="filter-date"
                      className="admin-filter-input"
                      value={taskFilters.date}
                      onChange={(e) => setTaskFilters({ ...taskFilters, date: e.target.value })}
                    />
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="filter-employee">Employee</label>
                    <div className="admin-filter-dropdown" ref={employeeFilterRef}>
                      <button
                        type="button"
                        className={`admin-filter-dropdown-trigger ${showEmployeeFilterDropdown ? 'open' : ''}`}
                        onClick={() => setShowEmployeeFilterDropdown(!showEmployeeFilterDropdown)}
                      >
                        {taskFilters.employeeId 
                          ? employees.find(emp => emp.id.toString() === taskFilters.employeeId) 
                            ? `${employees.find(emp => emp.id.toString() === taskFilters.employeeId)!.name} ${employees.find(emp => emp.id.toString() === taskFilters.employeeId)!.surname}`
                            : 'All Employees'
                          : 'All Employees'}
                      </button>
                      {showEmployeeFilterDropdown && (
                        <div className="admin-filter-dropdown-menu">
                          <div
                            className={`admin-filter-dropdown-item ${!taskFilters.employeeId ? 'selected' : ''}`}
                            onClick={() => {
                              setTaskFilters({ ...taskFilters, employeeId: '' });
                              setShowEmployeeFilterDropdown(false);
                            }}
                          >
                            All Employees
                          </div>
                          {employees.map((employee) => (
                            <div
                              key={employee.id}
                              className={`admin-filter-dropdown-item ${taskFilters.employeeId === employee.id.toString() ? 'selected' : ''}`}
                              onClick={() => {
                                setTaskFilters({ ...taskFilters, employeeId: employee.id.toString() });
                                setShowEmployeeFilterDropdown(false);
                              }}
                            >
                              {employee.name} {employee.surname}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="filter-place">Place</label>
                    <input
                      type="text"
                      id="filter-place"
                      className="admin-filter-input"
                      placeholder="Filter by location"
                      value={taskFilters.location}
                      onChange={(e) => setTaskFilters({ ...taskFilters, location: e.target.value })}
                    />
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="filter-deleted">Status</label>
                    <div className="admin-filter-dropdown" ref={statusFilterRef}>
                      <button
                        type="button"
                        className={`admin-filter-dropdown-trigger ${showStatusFilterDropdown ? 'open' : ''}`}
                        onClick={() => setShowStatusFilterDropdown(!showStatusFilterDropdown)}
                      >
                        {taskFilters.deleted === 'non-deleted' ? 'Non-deleted only' 
                          : taskFilters.deleted === 'deleted' ? 'Deleted only' 
                          : 'All tasks'}
                      </button>
                      {showStatusFilterDropdown && (
                        <div className="admin-filter-dropdown-menu">
                          <div
                            className={`admin-filter-dropdown-item ${taskFilters.deleted === 'non-deleted' ? 'selected' : ''}`}
                            onClick={() => {
                              const newFilters = { ...taskFilters, deleted: 'non-deleted' as const };
                              setTaskFilters(newFilters);
                              fetchTasks(newFilters);
                              setShowStatusFilterDropdown(false);
                            }}
                          >
                            Non-deleted only
                          </div>
                          <div
                            className={`admin-filter-dropdown-item ${taskFilters.deleted === 'deleted' ? 'selected' : ''}`}
                            onClick={() => {
                              const newFilters = { ...taskFilters, deleted: 'deleted' as const };
                              setTaskFilters(newFilters);
                              fetchTasks(newFilters);
                              setShowStatusFilterDropdown(false);
                            }}
                          >
                            Deleted only
                          </div>
                          <div
                            className={`admin-filter-dropdown-item ${taskFilters.deleted === 'all' ? 'selected' : ''}`}
                            onClick={() => {
                              const newFilters = { ...taskFilters, deleted: 'all' as const };
                              setTaskFilters(newFilters);
                              fetchTasks(newFilters);
                              setShowStatusFilterDropdown(false);
                            }}
                          >
                            All tasks
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="admin-filter-btn"
                    onClick={() => fetchTasks()}
                    disabled={isLoadingTasks}
                  >
                    Apply Filters
                  </button>
                  <button 
                    type="button" 
                    className="admin-secondary-btn"
                    onClick={() => {
                      const clearedFilters = {
                        date: '',
                        employeeId: '',
                        location: '',
                        deleted: 'non-deleted' as const,
                      };
                      setTaskFilters(clearedFilters);
                      fetchTasks(clearedFilters);
                    }}
                    disabled={isLoadingTasks}
                  >
                    Clear Filters
                  </button>
                </div>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Task ID</th>
                        <th>Task Code</th>
                        <th>Quantity</th>
                        <th>Employee</th>
                        <th>Location</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Deleted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingTasks ? (
                        <tr>
                          <td colSpan={9} className="admin-table-empty">
                            Loading tasks...
                          </td>
                        </tr>
                      ) : tasks.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="admin-table-empty">
                            No tasks found
                          </td>
                        </tr>
                      ) : (
                        tasks.map((task) => {
                          const employee = employees.find(emp => emp.id === task.employee_id);
                          const taskDate = new Date(task.created_at);
                          const formattedDate = taskDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          });
                          
                          return (
                            <tr key={task.id}>
                              <td>{task.id}</td>
                              <td>{task.task_code}</td>
                              <td>{task.quantity}</td>
                              <td>
                                {employee ? `${employee.name} ${employee.surname}` : `ID: ${task.employee_id}`}
                              </td>
                              <td>{task.location || '-'}</td>
                              <td>{formattedDate}</td>
                              <td>
                                <span className={`admin-status-badge ${task.is_deleted ? 'deleted' : task.forced_submit ? 'forced' : 'active'}`}>
                                  {task.is_deleted ? 'Deleted' : task.forced_submit ? 'Forced' : 'Active'}
                                </span>
                              </td>
                              <td>
                                <span className={`admin-status-badge ${task.is_deleted ? 'deleted' : 'active'}`}>
                                  {task.is_deleted ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  {!task.is_deleted && (
                                    <>
                                      <button 
                                        type="button" 
                                        className="admin-secondary-btn"
                                        onClick={() => handleOpenEditTaskModal(task)}
                                        disabled={isSubmitting || isDeleting}
                                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        type="button" 
                                        className="admin-action-btn"
                                        onClick={() => handleDeleteTaskClick(task)}
                                        disabled={isSubmitting || isDeleting}
                                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add New Employee</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddEmployee} className="admin-modal-form">
              <div className="admin-form-group">
                <label htmlFor="employee-name">Name *</label>
                <input
                  type="text"
                  id="employee-name"
                  className="admin-form-input"
                  value={employeeForm.name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="employee-surname">Surname *</label>
                <input
                  type="text"
                  id="employee-surname"
                  className="admin-form-input"
                  value={employeeForm.surname}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, surname: e.target.value })}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="employee-role">Role</label>
                <div className="admin-filter-dropdown" ref={roleDropdownRef}>
                  <button
                    type="button"
                    className={`admin-filter-dropdown-trigger ${showRoleDropdown ? 'open' : ''}`}
                    onClick={() => !isSubmitting && setShowRoleDropdown(!showRoleDropdown)}
                    disabled={isSubmitting}
                    style={{ 
                      width: '100%',
                      opacity: isSubmitting ? 0.5 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {employeeForm.role === 'admin' ? 'Admin' : 'Employee'}
                  </button>
                  {showRoleDropdown && !isSubmitting && (
                    <div className="admin-filter-dropdown-menu">
                      <div
                        className={`admin-filter-dropdown-item ${employeeForm.role === 'employee' ? 'selected' : ''}`}
                        onClick={() => {
                          setEmployeeForm({ ...employeeForm, role: 'employee' });
                          setShowRoleDropdown(false);
                        }}
                      >
                        Employee
                      </div>
                      <div
                        className={`admin-filter-dropdown-item ${employeeForm.role === 'admin' ? 'selected' : ''}`}
                        onClick={() => {
                          setEmployeeForm({ ...employeeForm, role: 'admin' });
                          setShowRoleDropdown(false);
                        }}
                      >
                        Admin
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-form-group">
                <label htmlFor="employee-external-id">External ID (Optional)</label>
                <input
                  type="text"
                  id="employee-external-id"
                  className="admin-form-input"
                  value={employeeForm.external_id || ''}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, external_id: e.target.value || null })}
                  placeholder="Optional"
                  disabled={isSubmitting}
                />
              </div>
              {submitMessage && (
                <div className={`admin-form-message ${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-primary-btn"
                  disabled={isSubmitting || !employeeForm.name.trim() || !employeeForm.surname.trim()}
                >
                  {isSubmitting ? 'Creating...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Confirmation Modal */}
      {showAdminConfirmationModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Confirm Admin Account Creation</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-form">
              <div className="admin-confirmation-warning">
                <p>
                  Admin has rights to create and delete employees, create, delete, and edit tasks.
                </p>
                <p>
                  <strong>Are you sure you want to create an admin account?</strong>
                </p>
                <p>
                  If yes, type in below: <strong>"yes, i want to create an admin account"</strong>
                </p>
              </div>
              <div className="admin-form-group">
                <label htmlFor="admin-confirmation">Confirmation</label>
                <input
                  type="text"
                  id="admin-confirmation"
                  className="admin-form-input"
                  value={adminConfirmationText}
                  onChange={(e) => setAdminConfirmationText(e.target.value)}
                  placeholder="yes, i want to create an admin account"
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={handleAdminConfirmation}
                  disabled={isSubmitting || adminConfirmationText.trim().toLowerCase() !== 'yes, i want to create an admin account'}
                >
                  Confirm & Create Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {employeeToResetPassword && (
        <div className="admin-modal-overlay" onClick={() => !isResettingPassword && setEmployeeToResetPassword(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Reset Employee Password</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={() => setEmployeeToResetPassword(null)}
                disabled={isResettingPassword}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-form">
              <div className="admin-confirmation-warning">
                <p>
                  Are you sure you want to reset the password for this employee?
                </p>
                <p>
                  <strong>{employeeToResetPassword.name} {employeeToResetPassword.surname}</strong>
                </p>
                <p style={{ marginTop: '16px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  The employee will need to set a new password using the "New Employee" tab on the login page.
                </p>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={() => setEmployeeToResetPassword(null)}
                  disabled={isResettingPassword}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={handleResetPassword}
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Employee Confirmation Modal */}
      {employeeToDelete && (
        <div className="admin-modal-overlay" onClick={(e) => {
          // Only close if not deleting and no error message
          if (!isDeleting && !submitMessage) {
            handleCloseModal();
          }
        }}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Confirm Employee Deletion</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-form">
              {submitMessage && (
                <div className={`admin-form-message ${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}
              <div className="admin-confirmation-warning">
                <p>
                  This action is permanent. Are you sure you want to delete this employee?
                </p>
                <p>
                  <strong>{employeeToDelete.name} {employeeToDelete.surname}</strong>
                </p>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-action-btn"
                  onClick={handleDeleteEmployee}
                  disabled={isDeleting}
                  style={{ margin: 0 }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Employee'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="admin-modal-header">
              <h3>Add New Task</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddTask} className="admin-modal-form">
              <div className="admin-form-group">
                <label htmlFor="task-code">Task *</label>
                <div ref={taskDropdownRef} style={{ position: 'relative' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <input
                      type="text"
                      id="task-code"
                      className="admin-form-input"
                      value={taskSearchQuery}
                      onChange={(e) => {
                        setTaskSearchQuery(e.target.value);
                        setShowTaskDropdown(true);
                        if (!e.target.value) {
                          setTaskForm({ ...taskForm, task_code: '' });
                        }
                      }}
                      onFocus={() => {
                        if (!showTaskDropdown) {
                          setShowTaskDropdown(true);
                        }
                      }}
                      placeholder="Type to search tasks..."
                      required
                      disabled={isSubmitting || isLoadingTaskDefinitions}
                      autoComplete="off"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                      disabled={isSubmitting || isLoadingTaskDefinitions}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        fontSize: '1.2rem',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {showTaskDropdown ? '▲' : '▼'}
                    </button>
                  </div>
                  {showTaskDropdown && (
                    <div className="admin-dropdown">
                      {isLoadingTaskDefinitions ? (
                        <div className="admin-dropdown-loading">Loading tasks...</div>
                      ) : filteredTaskDefinitions.length === 0 ? (
                        <div className="admin-dropdown-empty">
                          {taskSearchQuery ? 'No tasks found matching your search' : 'No tasks available'}
                        </div>
                      ) : (
                        filteredTaskDefinitions.map((taskDef) => (
                          <div
                            key={taskDef.task_code}
                            className={`admin-dropdown-item ${taskForm.task_code === taskDef.task_code ? 'selected' : ''}`}
                            onClick={() => handleSelectTask(taskDef)}
                          >
                            <div className="admin-dropdown-item-code">
                              <span>{taskDef.task_code}</span>
                              {taskForm.task_code === taskDef.task_code && (
                                <span className="selected-indicator">✓ Selected</span>
                              )}
                            </div>
                            <div className="admin-dropdown-item-description">
                              {taskDef.beschrijving}
                            </div>
                            <div className="admin-dropdown-item-meta">
                              <span>Unit: {taskDef.eenheid}</span>
                              <span>Price: €{taskDef.prijs_per_eenheid_in_euro}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {selectedTaskDefinition && !showTaskDropdown && (
                    <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'rgba(245, 158, 66, 0.1)', border: '1px solid rgba(245, 158, 66, 0.3)', borderRadius: '8px', fontSize: '0.9rem' }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px' }}>Selected: {selectedTaskDefinition.task_code}</div>
                      <div style={{ color: 'var(--text-main)', marginBottom: '6px' }}>{selectedTaskDefinition.beschrijving}</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                          Unit: <strong>{selectedTaskDefinition.eenheid}</strong>
                        </span>
                        <span style={{ padding: '4px 8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                          Price: <strong>€{selectedTaskDefinition.prijs_per_eenheid_in_euro}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-form-group">
                <label htmlFor="task-quantity">
                  Quantity {selectedTaskDefinition && `(${selectedTaskDefinition.eenheid})`} *
                </label>
                <input
                  type="number"
                  id="task-quantity"
                  className="admin-form-input"
                  value={taskForm.quantity || ''}
                  onChange={(e) => setTaskForm({ ...taskForm, quantity: parseFloat(e.target.value) || 0 })}
                  step="0.01"
                  min="0"
                  required
                  disabled={isSubmitting}
                  placeholder={selectedTaskDefinition ? `Enter quantity in ${selectedTaskDefinition.eenheid}` : 'Enter quantity'}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="task-employee">Employee *</label>
                <div className="admin-filter-dropdown" ref={employeeTaskRef}>
                  <button
                    type="button"
                    className={`admin-filter-dropdown-trigger ${showEmployeeTaskDropdown ? 'open' : ''}`}
                    onClick={() => !isSubmitting && setShowEmployeeTaskDropdown(!showEmployeeTaskDropdown)}
                    disabled={isSubmitting}
                    style={{ 
                      width: '100%',
                      opacity: isSubmitting ? 0.5 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {taskForm.employee_id 
                      ? employees.find(emp => emp.id === taskForm.employee_id)
                        ? `${employees.find(emp => emp.id === taskForm.employee_id)!.name} ${employees.find(emp => emp.id === taskForm.employee_id)!.surname}`
                        : 'Select an employee'
                      : 'Select an employee'}
                  </button>
                  {showEmployeeTaskDropdown && !isSubmitting && (
                    <div className="admin-filter-dropdown-menu">
                      <div
                        className={`admin-filter-dropdown-item ${!taskForm.employee_id ? 'selected' : ''}`}
                        onClick={() => {
                          setTaskForm({ ...taskForm, employee_id: 0 });
                          setShowEmployeeTaskDropdown(false);
                        }}
                      >
                        Select an employee
                      </div>
                      {employees.map((employee) => (
                        <div
                          key={employee.id}
                          className={`admin-filter-dropdown-item ${taskForm.employee_id === employee.id ? 'selected' : ''}`}
                          onClick={() => {
                            setTaskForm({ ...taskForm, employee_id: employee.id });
                            setShowEmployeeTaskDropdown(false);
                          }}
                        >
                          {employee.name} {employee.surname}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!taskForm.employee_id && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: '4px' }}>
                    Employee is required
                  </div>
                )}
              </div>
              <div className="admin-form-group">
                <label htmlFor="task-location">Location</label>
                <input
                  type="text"
                  id="task-location"
                  className="admin-form-input"
                  value={taskForm.location || ''}
                  onChange={(e) => setTaskForm({ ...taskForm, location: e.target.value || null })}
                  placeholder="Optional"
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="task-comments">Comments</label>
                <textarea
                  id="task-comments"
                  className="admin-form-input"
                  value={taskForm.comments || ''}
                  onChange={(e) => setTaskForm({ ...taskForm, comments: e.target.value || null })}
                  placeholder="Optional"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={taskForm.forced_submit || false}
                    onChange={(e) => setTaskForm({ ...taskForm, forced_submit: e.target.checked })}
                    disabled={isSubmitting}
                  />
                  <span>Force Submit</span>
                </label>
              </div>
              {submitMessage && (
                <div className={`admin-form-message ${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-primary-btn"
                  disabled={
                    isSubmitting || 
                    !taskForm.task_code || 
                    !taskForm.quantity || 
                    taskForm.quantity <= 0 ||
                    !taskForm.employee_id
                  }
                >
                  {isSubmitting ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && taskToEdit && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="admin-modal-header">
              <h3>Edit Task #{taskToEdit.id}</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateTask} className="admin-modal-form">
              <div className="admin-form-group">
                <label>Task Code</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={taskToEdit.task_code}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="edit-task-quantity">Quantity *</label>
                <input
                  type="number"
                  id="edit-task-quantity"
                  className="admin-form-input"
                  value={editTaskForm.quantity || ''}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, quantity: parseFloat(e.target.value) || 0 })}
                  step="0.01"
                  min="0"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="edit-task-location">Location</label>
                <input
                  type="text"
                  id="edit-task-location"
                  className="admin-form-input"
                  value={editTaskForm.location || ''}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, location: e.target.value || null })}
                  placeholder="Optional"
                  disabled={isSubmitting}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="edit-task-comments">Comments</label>
                <textarea
                  id="edit-task-comments"
                  className="admin-form-input"
                  value={editTaskForm.comments || ''}
                  onChange={(e) => setEditTaskForm({ ...editTaskForm, comments: e.target.value || null })}
                  placeholder="Optional"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              {submitMessage && (
                <div className={`admin-form-message ${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-primary-btn"
                  disabled={isSubmitting || !editTaskForm.quantity || editTaskForm.quantity <= 0}
                >
                  {isSubmitting ? 'Updating...' : 'Update Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Task Confirmation Modal */}
      {taskToDelete && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Confirm Task Deletion</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-form">
              <div className="admin-confirmation-warning">
                <p>
                  This will perform a soft delete. The task will be marked as deleted but not permanently removed.
                </p>
                <p>
                  <strong>Task #{taskToDelete.id}</strong> - {taskToDelete.task_code}
                </p>
                {taskToDelete.location && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '8px' }}>
                    Location: {taskToDelete.location}
                  </p>
                )}
              </div>
              {deleteErrorMessage && (
                <div className={`admin-form-message error`}>
                  {deleteErrorMessage}
                </div>
              )}
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-action-btn"
                  onClick={handleConfirmDeleteTask}
                  disabled={isDeleting}
                  style={{ margin: 0 }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Task'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export to Invoice Modal */}
      {showInvoiceModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="admin-modal-header">
              <h3>Export to Invoice</h3>
              <button 
                type="button" 
                className="admin-modal-close"
                onClick={handleCloseModal}
                disabled={isGeneratingInvoice}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-form">
              <div className="admin-form-group">
                <label htmlFor="invoice-start-date">Start Date *</label>
                <input
                  type="date"
                  id="invoice-start-date"
                  className="admin-form-input"
                  value={invoiceDateRange.startDate}
                  onChange={(e) => setInvoiceDateRange({ ...invoiceDateRange, startDate: e.target.value })}
                  required
                  disabled={isGeneratingInvoice}
                />
              </div>
              <div className="admin-form-group">
                <label htmlFor="invoice-end-date">End Date *</label>
                <input
                  type="date"
                  id="invoice-end-date"
                  className="admin-form-input"
                  value={invoiceDateRange.endDate}
                  onChange={(e) => setInvoiceDateRange({ ...invoiceDateRange, endDate: e.target.value })}
                  required
                  disabled={isGeneratingInvoice}
                />
              </div>
              <div style={{ padding: '12px', backgroundColor: 'rgba(245, 158, 66, 0.1)', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '8px' }}>
                <p style={{ margin: 0, color: 'var(--text-main)' }}>
                  Only non-deleted tasks within the selected date range will be included in the invoice.
                </p>
              </div>
              {invoiceErrorMessage && (
                <div className={`admin-form-message error`}>
                  {invoiceErrorMessage}
                </div>
              )}
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={handleCloseModal}
                  disabled={isGeneratingInvoice}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={handleExportToInvoice}
                  disabled={
                    isGeneratingInvoice || 
                    !invoiceDateRange.startDate || 
                    !invoiceDateRange.endDate
                  }
                >
                  {isGeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConsole;

